import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { DIALOG_DATA, DialogRef } from "@angular/cdk/dialog";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faChevronLeft, faChevronRight, faGear } from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { InfoModalButtonComponent } from "../../../info-modal/info-modal-button.component";
import { InfoModalData } from "../../../info-modal/info-modal.model";
import { MarketplaceChangelog } from "../../../../models/marketplace-changelog.model";
import { MarketplaceService } from "../../../../services/marketplace.service";
import { Page } from "../../../../models/page.model";
import { HttpErrorResponse } from "@angular/common/http";
import { Subject, takeUntil } from "rxjs";

export interface MarketplaceChangelogModalData {
  marketplaceId: number;
  marketplaceName: string;
}

@Component({
  selector: "app-marketplace-changelog-modal",
  standalone: true,
  imports: [CommonModule, FaIconComponent, InfoModalButtonComponent],
  templateUrl: "./marketplace-changelog-modal.component.html"
})
export class MarketplaceChangelogModalComponent implements OnInit, OnDestroy {
  private readonly onDestroy$: Subject<void> = new Subject<void>();
  protected readonly faXmark: IconDefinition = faXmark;
  protected readonly faChevronLeft: IconDefinition = faChevronLeft;
  protected readonly faChevronRight: IconDefinition = faChevronRight;
  protected readonly faGear: IconDefinition = faGear;

  protected readonly changelogInfo: InfoModalData = {
    title: "Marketplace Changelog",
    subtitle: "A read-only audit trail of every configuration change made to this marketplace.",
    sections: [
      {
        heading: "What Each Entry Shows",
        items: [
          {
            label: "Field Changed",
            description:
              "The specific configuration field that was modified, shown with a change category badge (Created, Settings, Appetite, or Priority)."
          },
          {
            label: "Timestamp",
            description:
              "The exact date and time the change was saved, displayed in your local timezone."
          },
          {
            label: "Previous Value",
            description:
              "The value the field held before the change. Shown with a red strikethrough or a 'Removed' list for collection fields such as States."
          },
          {
            label: "New Value",
            description:
              "The value the field was updated to. Shown in green or an 'Added' list for collection fields."
          },
          {
            label: "Changed By (by)",
            description: "The name of the user who saved the change."
          }
        ]
      },
      {
        heading: "Change Categories",
        items: [
          {
            label: "Created",
            description: "Logged once when the marketplace is first created."
          },
          {
            label: "Settings",
            description:
              "Changes made to the core identify of the Marketplace: e.g. name, phone number, API type, Price Modifier, etc."
          },
          {
            label: "Appetite",
            description:
              "Changes to the marketplaces appetite: e.g. states, NAICS codes, business line limits, insured status, personal and commercial lines settings, etc.."
          },
          {
            label: "Priority",
            description: "Changes to marketplace priority with respect to other marketplaces."
          }
        ]
      },
      {
        heading: "Pagination",
        items: [
          {
            label: "Entries Per Page",
            description:
              "The changelog displays up to 20 entries per page, ordered from most recent to oldest."
          },
          {
            label: "Navigating Pages",
            description:
              "Use the Previous and Next buttons in the footer to move between pages. The total entry count and current page number are shown so you can gauge how many changes have been recorded."
          }
        ]
      },
      {
        heading: "History Retention",
        items: [
          {
            label: "How Far Back",
            description: "All changes since tracking began are available in the log."
          }
        ]
      },
      {
        heading: "Read-Only Log",
        items: [
          {
            label: "No Actions Available",
            description:
              "The changelog is strictly read-only. Entries cannot be edited, deleted, or used to trigger a rollback from within this view. To revert a change, update the marketplace configuration manually on the Edit page."
          }
        ]
      },
      {
        heading: "When to Use the Changelog",
        items: [
          {
            label: "Audit Trail",
            description:
              "Verify who changed a setting and when — useful for compliance reviews or when a agency partner questions a configuration."
          },
          {
            label: "Troubleshooting",
            description:
              "Identify whether a recent configuration change is responsible for unexpected behavior, such as referrals stopping or appetite narrowing unexpectedly."
          },
          {
            label: "Rollback Awareness",
            description:
              "Determine the exact previous value of a setting before a change was made, so you can manually restore it on the Edit page if needed."
          }
        ]
      }
    ]
  };

  protected entries: MarketplaceChangelog[] = [];
  protected isLoading: boolean = true;
  protected hasError: boolean = false;
  protected currentPage: number = 1;
  protected pageSize: number = 20;
  protected totalElements: number = 0;
  protected totalPages: number = 0;

  constructor(
    public dialogRef: DialogRef<void>,
    @Inject(DIALOG_DATA) public data: MarketplaceChangelogModalData,
    private marketplaceService: MarketplaceService
  ) {}

  ngOnInit(): void {
    this.loadPage(0);
  }

  private loadPage(page: number): void {
    this.isLoading = true;
    this.hasError = false;

    this.marketplaceService
      .getMarketplaceChangelog(this.data.marketplaceId, page, this.pageSize)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (result: Page<MarketplaceChangelog>): void => {
          this.entries = result.content;
          this.totalElements = result.page.totalElements;
          this.totalPages = result.page.totalPages;
          this.currentPage = page + 1;
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.hasError = true;
          this.isLoading = false;
          console.error("Error loading changelog:", error);
        }
      });
  }

  protected nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.loadPage(this.currentPage);
    }
  }

  protected previousPage(): void {
    if (this.currentPage > 1) {
      this.loadPage(this.currentPage - 2);
    }
  }

  protected close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private static readonly COLLECTION_FIELDS = new Set([
    "State",
    "Excluded NAICS",
    "Excluded Source",
    "Email Recipient",
    "Email BCC Recipient"
  ]);

  protected isCollectionField(fieldName: string): boolean {
    return MarketplaceChangelogModalComponent.COLLECTION_FIELDS.has(fieldName);
  }

  protected get displayEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalElements);
  }

  protected parseCollectionValue(value: string | null | undefined): string[] {
    if (!value) return [];
    return value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }

  protected getCategoryClass(category: string): string {
    switch (category) {
      case "CREATED":
        return "bg-green-100 text-green-700";
      case "SETTINGS":
        return "bg-blue-100 text-blue-700";
      case "APPETITE":
        return "bg-purple-100 text-purple-700";
      case "PRIORITY":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  }

  protected getDotClass(category: string): string {
    switch (category) {
      case "CREATED":
        return "bg-green-500 ring-green-300";
      case "SETTINGS":
        return "bg-blue-500 ring-blue-300";
      case "APPETITE":
        return "bg-purple-500 ring-purple-300";
      case "PRIORITY":
        return "bg-orange-500 ring-orange-300";
      default:
        return "bg-gray-400 ring-gray-300";
    }
  }

  protected formatDate(dateString: string): string {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  }
}
